import { FieldRepository } from '@aipb/gulliver/castor/data-access';
import { CoreFieldInfoLang } from '@aipb/gulliver/castor/model';
import { FacetRepository, FacetService, SearchRepository, SearchService } from '@aipb/gulliver/catalog/data-access';
import { CatalogEnvService } from '@aipb/gulliver/catalog/env';
import { Facet, SearchTab } from '@aipb/gulliver/catalog/model';
import { LoadingProgressComponent } from '@aipb/shared/ui/loading';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnInit, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Observable } from 'rxjs';
import { countCheckedFacets } from '../../facet.util';
import { FacetListItemComponent } from '../facet-list-item/facet-list-item.component';
import { FacetFilterComponent } from '../facet-filter/facet-filter.component';
import { FacetBaseComponent } from '../facet-base.component';
import { MatButtonLoading } from '@ng-matero/extensions/button';

@UntilDestroy()
@Component({
  selector: 'cat-facet-list',
  templateUrl: './facet-list.component.html',
  styleUrls: ['./facet-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    ScrollingModule,
    FacetListItemComponent,
    TranslocoModule,
    LoadingProgressComponent,
    MatIconModule,
    FacetFilterComponent,
    MatButtonLoading
  ]
})
export class FacetListComponent extends FacetBaseComponent implements OnInit {
  @ViewChild(FacetFilterComponent, { static: true }) facetFilter: FacetFilterComponent;

  filteredValue = '';
  objectKeys = Object.keys;
  registerFields$: Observable<CoreFieldInfoLang[]>;
  translatableFacets: string[] = [];
  private searchService = inject(SearchService);
  private facetService = inject(FacetService);
  private facetRepository = inject(FacetRepository);
  loading$ = this.facetRepository.loading$;
  private searchRepository = inject(SearchRepository);
  private fieldRepository = inject(FieldRepository);
  private catalogEnv = inject(CatalogEnvService);
  private transloco = inject(TranslocoService);
  private cdr = inject(ChangeDetectorRef);

  constructor() {
    super();
    this.translatableFacets = this.catalogEnv.env.translatableFacets;
  }

  get isMapSearch(): boolean {
    return this.catalogEnv.hasMapSearch();
  }

  ngOnInit(): void {
    this.registerFields$ = this.fieldRepository.selectRegister(this.searchItem.coreId);

    this.transloco.langChanges$.pipe(untilDestroyed(this)).subscribe(() => {
      this.cdr.detectChanges();
    });
  }

  onToggleFacetCategory(key: string): void {
    this.facetRepository.toggleFacetCategory(this.facetItem.id, key);
  }

  onToggleFacet(fieldId: string, item: Facet): void {
    this.facetRepository.toggleFacet(this.facetItem.id, fieldId, item);

    const searchItem = this.searchRepository.getSearch(this.facetItem.id);
    this.searchService.updateFacetResultCount(searchItem, this.facetService.getFacetQuery(searchItem.id));
    this.searchService.updateFacetNotResultCount(searchItem, this.facetService.getFacetNotQuery(searchItem.id));
  }

  countChecked(facets: Facet[]) {
    return countCheckedFacets(facets);
  }

  onFilterValueChange(value: string): void {
    this.filteredValue = value;

    if (!this.searchItem) return;

    this.facetService.loadFacets(this.searchItem, this.filteredValue).subscribe();
  }

  onResetFacetFilter(): void {
    this.searchService.updateFacetResultCount(this.searchItem, this.searchItem.search.q);
    this.facetRepository.resetFacetFilter(this.searchItem.id);
    this.facetFilter.clear();
  }

  onSearch(): void {
    const query = this.facetService.getFacetQuery(this.searchItem.id);

    const searchTab: SearchTab = {
      solrSearchParams: {
        query: query,
        coreId: this.searchItem.coreId,
        sort: this.searchItem.search_sort
      },
      settings: {}
    };

    if (this.searchItem.coordinates) {
      const coordinates = this.searchItem.coordinates;

      if (coordinates && coordinates.length > 0) {
        searchTab.coordinates = coordinates;
        searchTab.solrSearchParams = {
          ...searchTab.solrSearchParams,
          spatialFilter: {
            spatialType: 'rectangle',
            spatialField: 'Coordinates',
            rectangle: coordinates.join(',')
          }
        };
      }
    }

    this.searchService.addTab(searchTab, true);
  }

  onNotSearch() {
    const query = this.facetService.getFacetNotQuery(this.searchItem.id);

    const searchTab: SearchTab = {
      solrSearchParams: {
        query: query,
        coreId: this.searchItem.coreId,
        sort: this.searchItem.search_sort
      },
      settings: {}
    };

    if (this.isMapSearch) {
      const coordinates = this.searchItem.coordinates;

      if (coordinates && coordinates.length > 0) {
        searchTab.coordinates = coordinates;
        searchTab.solrSearchParams = {
          ...searchTab.solrSearchParams,
          spatialFilter: {
            spatialType: 'rectangle',
            spatialField: 'Coordinates',
            rectangle: coordinates.join(',')
          }
        };
      }
    }

    this.searchService.addTab(searchTab, true);
  }

  isEmpty(): boolean {
    if (!this.facetItem) {
      return true;
    }

    if (!this.facetItem.facets) {
      return true;
    }

    if (this.facetItem.facets && Object.keys(this.facetItem.facets).length === 0) {
      return true;
    }

    return Object.keys(this.facetItem.facets).filter((key) => this.facetItem.facets[key].items.length > 0).length === 0;
  }
}
